import {
	type AddTwoFAMethodResponse,
	type AuthRepository,
	type AuthUser,
	InvalidAuthIdentifierError,
	InvalidAuthPasswordError,
	type LoginCredentials,
	type LoginResponse,
	type TokenPair,
	type TwoFactorMethod,
	TwoFactorMethodStatusOptions,
	type TwoFactorMethodType,
	TwoFactorMethodTypeOptions
} from '@contexts/auth/domain';
import {
	Clock,
	hasValue,
	isNil,
	stringValueObject,
	uuidValueObject
} from '@contexts/shared/domain';
import { StatusOptions } from '@contexts/users/domain';
import { users as usersRegistered } from '@contexts/users/infrastructure';

type TemporalToken = {
	token: string;
	userId: string;
};

export const userId = uuidValueObject().generate();

export const users: AuthUser[] = [generateValidAuthUser()];
export const temporalTokens: TemporalToken[] = [
	{
		token: 'token',
		userId: userId
	}
];
export const availableMethods: TwoFactorMethod[] = [
	{
		id: uuidValueObject().generate(),
		userId: userId,
		methodType: TwoFactorMethodTypeOptions.EMAIL,
		status: TwoFactorMethodStatusOptions.ACTIVE,
		isDefault: true,
		createdAt: Clock().getNow()
	},
	{
		id: uuidValueObject().generate(),
		userId: userId,
		methodType: TwoFactorMethodTypeOptions.TOTP,
		status: TwoFactorMethodStatusOptions.PENDING_VERIFICATION,
		isDefault: false,
		createdAt: Clock().getNow()
	},
	{
		id: uuidValueObject().generate(),
		userId: userId,
		methodType: TwoFactorMethodTypeOptions.PASSKEY,
		status: TwoFactorMethodStatusOptions.ACTIVE,
		isDefault: false,
		createdAt: Clock().getNow()
	}
];

/**
 * Cuentas para probar en local cada rol B2B. Entran con la misma contraseña y el
 * código 123456, y hacen que el usuario logado sea el del fixture indicado.
 */
export const ROLE_ACCOUNTS = [
	{ email: 'gestor@neki.com', fixtureUserEmail: 'test.user@neki.es' },
	{ email: 'operador@neki.com', fixtureUserEmail: 'elena.ruiz@neki.es' },
	{ email: 'personal@neki.com', fixtureUserEmail: 'lucia.prieto@neki.es' }
] as const;

const ROLE_ACCOUNTS_PASSWORD = 'Neki1234!';
const LOGGED_USER_STORAGE_KEY = 'neki.inMemory.loggedUserId';

export let isLogged = true;
export let isSuplanting = false;
export let suplantingUserId: string | null = null;
/** Usuario del fixture con el que se ha entrado; null = usuario por defecto. */
export let loggedUserId: string | null = readStoredLoggedUserId();

let pendingLoggedUserId: string | null = null;

function readStoredLoggedUserId(): string | null {
	try {
		return sessionStorage.getItem(LOGGED_USER_STORAGE_KEY);
	} catch {
		return null;
	}
}

function storeLoggedUserId(id: string | null): void {
	loggedUserId = id;
	try {
		if (id) sessionStorage.setItem(LOGGED_USER_STORAGE_KEY, id);
		else sessionStorage.removeItem(LOGGED_USER_STORAGE_KEY);
	} catch {
		// sessionStorage no disponible: la sesión dura hasta recargar.
	}
}

export function inMemoryAuthRepositoryBuilder(): AuthRepository {
	return {
		login: async ({ email, password }: LoginCredentials): Promise<LoginResponse> => {
			const roleAccount = ROLE_ACCOUNTS.find((account) => account.email === email);
			if (roleAccount) {
				if (password !== ROLE_ACCOUNTS_PASSWORD) {
					throw new InvalidAuthPasswordError(password);
				}

				pendingLoggedUserId =
					usersRegistered.find((user) => user.email === roleAccount.fixtureUserEmail)
						?.id ?? null;
				isSuplanting = false;
				suplantingUserId = null;
				return Promise.resolve({ token: 'token', availableMethods });
			}

			const user = users.find((storedUser) => {
				if (storedUser.email === email) {
					return storedUser;
				}

				return null;
			});

			if (isNil(user)) {
				throw new InvalidAuthIdentifierError(email);
			}

			if (password !== user.password) {
				throw new InvalidAuthPasswordError(password);
			}

			temporalTokens.push({
				token: uuidValueObject().generate(),
				userId: user.userId
			});

			pendingLoggedUserId = null;
			isSuplanting = false;
			suplantingUserId = null;
			return Promise.resolve({ token: 'token', availableMethods });
		},
		logout: async (): Promise<void> => {
			isLogged = false;
			isSuplanting = false;
			suplantingUserId = null;
			storeLoggedUserId(null);
			return Promise.resolve();
		},
		signup: async (signupRequest): Promise<string> => {
			const newUser: AuthUser = {
				userId: uuidValueObject().generate(),
				email: stringValueObject().validate('email', signupRequest.email),
				password: stringValueObject().validate('password', signupRequest.password),
				role: stringValueObject().validate('role', 'user'),
				status: stringValueObject().validate('status', StatusOptions.NOT_VERIFIED)
			};

			users.push(newUser);

			const newTemporalToken: TemporalToken = {
				token: uuidValueObject().generate(),
				userId: newUser.userId
			};

			temporalTokens.push(newTemporalToken);

			return Promise.resolve(newTemporalToken.token);
		},
		twoFactorLogin: async (token: string, code: string): Promise<TokenPair> => {
			const temporalToken = temporalTokens.find(
				(temporalToken) => temporalToken.token === token
			);

			if (isNil(temporalToken)) {
				throw new Error('Temporal token not found');
			}

			if (code !== '123456') {
				throw new Error('Invalid 2FA code');
			}

			isLogged = true;
			storeLoggedUserId(pendingLoggedUserId);

			return generateTokenPair();
		},
		refresh: async (): Promise<TokenPair> => {
			if (!isLogged) {
				throw new Error('User not logged');
			}

			return generateTokenPair();
		},
		activateUser: async (token: string, password: string): Promise<LoginResponse> => {
			const temporalToken = temporalTokens.find(
				(temporalToken) => temporalToken.token === token
			);

			if (isNil(temporalToken)) {
				throw new Error('Temporal token not found');
			}

			const userIndex = users.findIndex(
				(storedUser) => storedUser.userId === temporalToken.userId
			);

			if (userIndex === -1) {
				throw new Error('User not found');
			}

			users[userIndex].password = password;

			return Promise.resolve({ token: 'token', availableMethods });
		},
		revertUserActivation: async (token: string): Promise<void> => {
			const temporalToken = temporalTokens.find(
				(temporalToken) => temporalToken.token === token
			);

			if (isNil(temporalToken)) {
				throw new Error('Temporal token not found');
			}

			const userIndex = users.findIndex(
				(storedUser) => storedUser.userId === temporalToken.userId
			);

			if (userIndex === -1) {
				throw new Error('User not found');
			}

			return Promise.resolve();
		},
		update2FAMethod: async (
			methodId: string,
			status?: string,
			isDefault?: boolean
		): Promise<TwoFactorMethod> => {
			const methodIndex = availableMethods.findIndex(
				(storedMethod) => storedMethod.id === methodId
			);

			if (methodIndex === -1) {
				throw new Error('2FA Method not found');
			}

			if (hasValue(status)) {
				availableMethods[methodIndex].status = status as TwoFactorMethodStatusOptions;

				if (status === TwoFactorMethodStatusOptions.DISABLED) {
					if (availableMethods[methodIndex].isDefault) {
						availableMethods[methodIndex].isDefault = false;

						const emailMethod = availableMethods.find(
							(method) =>
								method.methodType === TwoFactorMethodTypeOptions.EMAIL &&
								method.id !== methodId &&
								method.userId === availableMethods[methodIndex].userId
						);

						if (emailMethod) {
							emailMethod.isDefault = true;
						}
					}
				}
			}

			if (hasValue(isDefault)) {
				if (isDefault === true) {
					availableMethods.forEach((method) => {
						if (method.userId === availableMethods[methodIndex].userId) {
							method.isDefault = method.id === methodId;
						}
					});
				} else {
					availableMethods[methodIndex].isDefault = false;
				}
			}

			return Promise.resolve(availableMethods[methodIndex]);
		},

		delete2FAMethod: async (methodId: string): Promise<string> => {
			const methodIndex = availableMethods.findIndex(
				(method) => method.id === methodId && method.userId === users[0].userId
			);

			if (methodIndex === -1) {
				throw new Error('2FA method not found');
			}

			const method = availableMethods[methodIndex];
			const activeMethods = availableMethods.filter(
				(userMethod) =>
					userMethod.userId === users[0].userId &&
					userMethod.status === TwoFactorMethodStatusOptions.ACTIVE &&
					userMethod.id !== method.id
			);

			if (activeMethods.length === 0) {
				throw new Error('User must keep at least one active 2FA method');
			}

			availableMethods.splice(methodIndex, 1);

			if (method.isDefault) {
				const fallback = availableMethods.find(
					(userMethod) =>
						userMethod.userId === users[0].userId &&
						userMethod.status === TwoFactorMethodStatusOptions.ACTIVE
				);

				if (fallback) {
					fallback.isDefault = true;
				}
			}

			return Promise.resolve(methodId);
		},

		addTwoFactorMethod: async (
			methodType: TwoFactorMethodType
		): Promise<AddTwoFAMethodResponse> => {
			const existingMethod = availableMethods.find(
				(method) => method.methodType === methodType && method.userId === users[0].userId
			);
			if (hasValue(existingMethod)) {
				throw new Error('2FA method already exists');
			}
			const newMethod: TwoFactorMethod = {
				id: uuidValueObject().generate(),
				userId: users[0].userId,
				methodType,
				status: TwoFactorMethodStatusOptions.ACTIVE,
				isDefault: false,
				createdAt: Date.now()
			};

			availableMethods.push(newMethod);

			switch (methodType) {
				case TwoFactorMethodTypeOptions.TOTP:
					return Promise.resolve({
						token: 'token',
						secret: 'secret',
						qr: 'data:image/png;base64,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'
					});
				case TwoFactorMethodTypeOptions.EMAIL:
					return Promise.resolve({
						token: 'token',
						channel: 'email'
					});
				case TwoFactorMethodTypeOptions.PASSKEY:
					return Promise.resolve({
						token: 'token',
						channel: 'passkey'
					});

				default:
					throw new Error('Invalid 2FA method type');
			}
		},
		getUserMethods: async (): Promise<TwoFactorMethod[]> => {
			return availableMethods.filter((method) => method.userId === users[0].userId);
		},
		changePassword: async (password: string): Promise<void> => {
			users[0].password = password;
			return Promise.resolve();
		},
		recoveryPassword: async (newPassword: string, token: string): Promise<void> => {
			const temporalToken = temporalTokens.find(
				(temporalToken) => temporalToken.token === token
			);

			if (isNil(temporalToken)) {
				throw new Error('Temporal token not found');
			}

			const userIndex = users.findIndex(
				(storedUser) => storedUser.userId === temporalToken.userId
			);

			if (userIndex === -1) {
				throw new Error('User not found');
			}

			users[userIndex].password = newPassword;

			return Promise.resolve();
		},
		resetPassword: async (): Promise<void> => {
			return Promise.resolve();
		},
		checkUserStatus: async (token: string): Promise<string> => {
			const temporalToken = temporalTokens.find(
				(temporalToken) => temporalToken.token === token
			);

			if (isNil(temporalToken)) {
				throw new Error('Temporal token not found');
			}

			const user = users.find((storedUser) => storedUser.userId === temporalToken.userId);

			if (isNil(user)) {
				throw new Error('User not found');
			}

			return user.status;
		},
		unlockUser: async (id: string): Promise<void> => {
			const userIndex = usersRegistered.findIndex((storedUser) => storedUser.id === id);

			if (userIndex === -1) {
				throw new Error('User not found');
			}

			usersRegistered[userIndex].status = StatusOptions.ACTIVE;
		},
		suplantUser: async (userId: string): Promise<TokenPair> => {
			if (!isLogged) {
				throw new Error('User not logged');
			}

			isSuplanting = true;
			suplantingUserId = userId;

			return generateTokenPair();
		},
		suplantUserByDeviceIdentificator: async (
			deviceIdentificator: string
		): Promise<TokenPair> => {
			isSuplanting = true;
			suplantingUserId = null;

			return generateTokenPair();
		},
		revertSuplant: async (): Promise<void> => {
			isSuplanting = false;
			suplantingUserId = null;
			return Promise.resolve();
		},
		generateApiKey: async (): Promise<string> => {
			return 'generated-api-key-123';
		}
	};
}

export function generateTokenPair(): TokenPair {
	const oneHourInMilliseconds = 60 * 60 * 1000;

	return {
		accessToken: uuidValueObject().generate(),
		refreshToken: uuidValueObject().generate(),
		tokenType: 'Bearer',
		expiresIn: 900,
		sessionExpiresAt: Clock().getNow() + oneHourInMilliseconds
	};
}

export function generateValidAuthUser(): AuthUser {
	return {
		userId: userId,
		email: stringValueObject().validate('email', 'admin@neki.com'),
		password: stringValueObject().validate('password', 'Neki1234!'),
		role: stringValueObject().validate('role', 'admin'),
		status: stringValueObject().validate('status', 'active')
	};
}

export function generateValidTwoFactorMethod({
	methodTypes = [
		TwoFactorMethodTypeOptions.TOTP,
		TwoFactorMethodTypeOptions.EMAIL,
		TwoFactorMethodTypeOptions.PASSKEY
	],
	defaultMethodType = TwoFactorMethodTypeOptions.TOTP
}): TwoFactorMethod[] {
	const methods: TwoFactorMethod[] = [];

	if (methodTypes.includes(TwoFactorMethodTypeOptions.TOTP)) {
		methods.push({
			id: uuidValueObject().generate(),
			methodType: TwoFactorMethodTypeOptions.TOTP,
			status:
				defaultMethodType === TwoFactorMethodTypeOptions.TOTP
					? TwoFactorMethodStatusOptions.ACTIVE
					: TwoFactorMethodStatusOptions.DISABLED,
			isDefault: defaultMethodType === TwoFactorMethodTypeOptions.TOTP,
			createdAt: Clock().getNow(),
			userId: users[0].userId
		});
	}

	if (methodTypes.includes(TwoFactorMethodTypeOptions.EMAIL)) {
		methods.push({
			id: uuidValueObject().generate(),
			methodType: TwoFactorMethodTypeOptions.EMAIL,
			status:
				defaultMethodType === TwoFactorMethodTypeOptions.EMAIL
					? TwoFactorMethodStatusOptions.ACTIVE
					: TwoFactorMethodStatusOptions.DISABLED,
			isDefault: defaultMethodType === TwoFactorMethodTypeOptions.EMAIL,
			userId: users[0].userId,
			createdAt: Clock().getNow()
		});
	}

	if (methodTypes.includes(TwoFactorMethodTypeOptions.PASSKEY)) {
		methods.push({
			id: uuidValueObject().generate(),
			methodType: TwoFactorMethodTypeOptions.PASSKEY,
			status:
				defaultMethodType === TwoFactorMethodTypeOptions.PASSKEY
					? TwoFactorMethodStatusOptions.ACTIVE
					: TwoFactorMethodStatusOptions.DISABLED,
			isDefault: defaultMethodType === TwoFactorMethodTypeOptions.PASSKEY,
			userId: users[0].userId,
			createdAt: Clock().getNow()
		});
	}

	return methods;
}
